// Schienen, Schwellen, Schotterbett und Nebengleise.
//
// Konzept §12.1: Du siehst nie eine Welt, du siehst einen Korridor. Die Strecke
// ist 7,5 km — gebaut wird nur ein gleitendes Fenster um den Spieler. Segmente
// von 40 m, recycelt, max. 2 Neubauten pro Frame. Genau im Zustand „auf dem
// fahrenden Zug sitzen" darf hier nichts ruckeln (80 % der Spielzeit).
//
// Draw-Call-Budget: pro Segment 3 Meshes (beide Schienen in einer Geo,
// Schwellen-InstancedMesh, Bett). ~21 aktive Segmente → ~63 Calls, plus
// Nebengleise nur im Hofbereich.

import * as THREE from 'three';
import {
  RAIL_GAUGE, RAIL_HEAD_WIDTH, SLEEPER_SPACING, TRACK_BED_HALF_WIDTH,
} from '../../shared/constants.js';
import {
  TRACK_LENGTH, MINE_TRACK_LENGTH, STATIONS, DEAD_END, JUNCTION, JUNCTION_MAIN_S,
  BRANCH_MAIN, BRANCH_MINE,
  trackFrame, trackPoint, trackCant, railTopY, projectToTrack,
} from '../../shared/track.js';
import { mat, matShiny, box, varyColor, STYLE_RULES } from '../render/style.js';
import { COL_SOLID, COL_STEP } from './collision.js';

// --- Konstanten -------------------------------------------------------------

const SEG_LEN = 40;
/** Sichtfenster entlang s — Konzept: relevant ~300 m, Puffer bis ±400 m. */
const VIEW_RADIUS = 400;
const SAMPLE_STEP = 2.0;
const BUILDS_PER_FRAME = 2;

// Stilregel: kein Detail unter 10 cm. Schienenkopf real 7 cm → optisch aufrunden.
const RAIL_W = Math.max(RAIL_HEAD_WIDTH, STYLE_RULES.minDetailSize);
const RAIL_H = 0.14;
const RAIL_TOP_OFFSET = 0.18; // muss mit railTopY (p.y + 0.18) übereinstimmen
const HALF_GAUGE = RAIL_GAUGE * 0.5;

const SLEEPER_LEN = 2.5;
const SLEEPER_H = 0.14;
const SLEEPER_W = 0.22;

const BALLAST_TOP_HALF = TRACK_BED_HALF_WIDTH * 0.78;
const BALLAST_BOT_HALF = TRACK_BED_HALF_WIDTH;
const BALLAST_H = 0.32;

const MAX_SAMPLES = Math.ceil(SEG_LEN / SAMPLE_STEP) + 2;
const MAX_SLEEPERS = Math.ceil(SEG_LEN / SLEEPER_SPACING) + 4;

// Weichen-Anlauf der Nebengleise: früher 22 m → ~32° Gabel (wie die Mine-Weiche
// vor dem Fix). Echte Hofweichen (EW 190/300): 9 m Versatz erst nach 50–80 m.
// Cosinus-Ease, max. Winkel ≈ atan((π/2)·full/T) → bei full=9 und T=90 ≈ 8,9°.
const SIDING_TAPER = 90;

// Modul-Puffer — nichts pro Frame allokieren (CONTRACT).
const _frame = {
  pos: { x: 0, y: 0, z: 0 },
  tangent: { x: 0, y: 0, z: 0 },
  right: { x: 0, y: 0, z: 0 },
  heading: 0,
};
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _mat4 = new THREE.Matrix4();
const _scale = new THREE.Vector3(1, 1, 1);
const _color = new THREE.Color();
const _focus = { x: 0, y: 0, z: 0, s: 0 };
const _startPos = trackPoint(STATIONS[0].s);
// Scratch für Sichtweiten-Tests der Nebenstrecke (kein Frame-Allok).
const _mineFocus = { x: 0, y: 0, z: 0, s: 0, near: false };
const _minePt = { x: 0, y: 0, z: 0 };

// Vorallokierte Sample-Zeile (wiederverwendet beim Segment-Bau).
const _samples = new Array(MAX_SAMPLES);
for (let i = 0; i < MAX_SAMPLES; i++) {
  _samples[i] = {
    s: 0, x: 0, y: 0, z: 0,
    tx: 0, ty: 0, tz: 0,
    rx: 0, rz: 0,
    heading: 0, cant: 0, lateral: 0,
  };
}

export class TrackSystem {
  async init(ctx) {
    this.group = new THREE.Group();
    this.group.name = 'track';
    ctx.scene.add(this.group);

    // Ein glänzendes Material + Vertexfarben: blanker Kopf hell, Flanken rostig.
    // Ein Draw Call pro Schienen-Mesh — Multi-Material sprengt das 60-Call-Budget.
    this.matRail = matShiny('rail', { vertexColors: true });
    this.matSleeper = mat('sleeper', { vertexColors: true });
    this.matBallast = mat('ballast');
    this.matBallastDark = mat('ballastDark');
    this.matRailRust = mat('railRust', { vertexColors: true });
    this.matSleeperWorn = mat('woodDark', { vertexColors: true });

    this.sleeperGeo = box(SLEEPER_LEN, SLEEPER_H, SLEEPER_W);

    /** @type {Map<number, object>} */
    this.active = new Map();
    /** @type {object[]} */
    this.pool = [];
    /** @type {number[]} */
    this.queue = [];
    this.queueSet = new Set();
    this.centerSeg = null;

    // Nebenstrecke (Copper Spur): eigenes Segment-Pool, gleiche Bauweise.
    // Meist außer Sichtweite — nur bauen, wenn der Fokus nahe ist.
    /** @type {Map<number, object>} */
    this.activeMine = new Map();
    /** @type {object[]} */
    this.poolMine = [];
    /** @type {number[]} */
    this.queueMine = [];
    this.queueMineSet = new Set();
    this.centerSegMine = null;
    this.mineGroup = new THREE.Group();
    this.mineGroup.name = 'branch-mine';
    this.group.add(this.mineGroup);

    this.sidingGroup = new THREE.Group();
    this.sidingGroup.name = 'sidings';
    this.group.add(this.sidingGroup);
    this._buildSidings();
    this._buildMineDeadEnd();
    this._buildJunctionMarker();
    this._registerColliders(ctx);

    // Erste Segmente um den Südbahnhof — Frame 0 darf nicht leer sein.
    const focus = this._focusOf(ctx);
    this._syncDesired(focus.s);
    this._drainQueue(20);
    this._updateMineBranch(ctx, focus);
  }

  /**
   * Schienenoberkante inkl. Überhöhung. Andere Systeme (Player, Train) greifen
   * hier an — delegiert an shared, damit Server und Client denselben Wert sehen.
   * CONTRACT/Aufgabe: `ctx.systems.track.railY(s, lateral)`.
   */
  railY(s, lateral = 0, branch = BRANCH_MAIN) {
    return railTopY(s, lateral, branch);
  }

  /** Alias — gleiche Semantik wie railY. */
  railTopAt(s, lateral = 0, branch = BRANCH_MAIN) {
    return railTopY(s, lateral, branch);
  }

  /** Liegt dieser Lateral-Versatz auf dem Schotterbett? */
  isOnBallast(lateral) {
    return Math.abs(lateral) <= TRACK_BED_HALF_WIDTH;
  }

  update(_dt, ctx) {
    const focus = this._focusOf(ctx);
    const seg = Math.floor(focus.s / SEG_LEN);
    if (seg !== this.centerSeg) {
      this.centerSeg = seg;
      this._syncDesired(focus.s);
    }
    // Hauptstrecke bekommt den Großteil des Build-Budgets; Mine teilt sich den Rest.
    this._drainQueue(BUILDS_PER_FRAME);
    this._updateMineBranch(ctx, focus);
    this._updateSidingVisibility(focus.s);
  }

  // --- Segmentverwaltung ----------------------------------------------------

  _syncDesired(s) {
    const half = Math.ceil(VIEW_RADIUS / SEG_LEN);
    const center = Math.floor(s / SEG_LEN);
    const maxSeg = Math.floor((TRACK_LENGTH - 0.01) / SEG_LEN);
    const desired = new Set();

    for (let i = center - half; i <= center + half; i++) {
      if (i < 0 || i > maxSeg) continue;
      desired.add(i);
      if (!this.active.has(i) && !this.queueSet.has(i)) {
        this.queue.push(i);
        this.queueSet.add(i);
      }
    }

    for (const [idx, handle] of this.active) {
      if (!desired.has(idx)) this._release(idx, handle);
    }

    // Veraltete Queue-Einträge verwerfen (Spieler ist weitergezogen).
    if (this.queue.length > 0) {
      const kept = [];
      for (const idx of this.queue) {
        if (desired.has(idx) && !this.active.has(idx)) kept.push(idx);
        else this.queueSet.delete(idx);
      }
      this.queue = kept;
    }
  }

  _drainQueue(maxBuilds) {
    let built = 0;
    while (built < maxBuilds && this.queue.length > 0) {
      const idx = this.queue.shift();
      this.queueSet.delete(idx);
      if (this.active.has(idx)) continue;
      if (this.centerSeg != null) {
        const half = Math.ceil(VIEW_RADIUS / SEG_LEN);
        if (Math.abs(idx - this.centerSeg) > half + 1) continue;
      }
      const handle = this._acquire();
      fillSegment(handle, idx, this._matsMain());
      this.group.add(handle.group);
      this.active.set(idx, handle);
      built++;
    }
  }

  _matsMain() {
    return {
      matRail: this.matRail,
      matSleeper: this.matSleeper,
      matBallast: this.matBallast,
      sleeperGeo: this.sleeperGeo,
      weathered: false,
      branch: BRANCH_MAIN,
    };
  }

  /** Verwahrloste Materialien für die aufgegebene Kupfermine. */
  _matsMine() {
    return {
      matRail: this.matRailRust,
      matSleeper: this.matSleeperWorn,
      matBallast: this.matBallastDark,
      sleeperGeo: this.sleeperGeo,
      weathered: true,
      branch: BRANCH_MINE,
    };
  }

  _acquire() {
    if (this.pool.length > 0) return this.pool.pop();
    return createSegmentHandle(this.sleeperGeo);
  }

  _release(idx, handle) {
    this.active.delete(idx);
    this.group.remove(handle.group);
    handle.group.visible = false;
    this.pool.push(handle);
    this.queueSet.delete(idx);
  }

  _acquireMine() {
    if (this.poolMine.length > 0) return this.poolMine.pop();
    return createSegmentHandle(this.sleeperGeo);
  }

  _releaseMine(idx, handle) {
    this.activeMine.delete(idx);
    this.mineGroup.remove(handle.group);
    handle.group.visible = false;
    this.poolMine.push(handle);
    this.queueMineSet.delete(idx);
  }

  // --- Nebenstrecke (Copper Spur) -------------------------------------------

  /**
   * Baut Mine-Segmente nur wenn der Fokus nahe genug ist.
   * Die 1,25 km sind sonst reiner Draw-Call-Ballast (Konzept §12.1 Korridor).
   */
  _updateMineBranch(ctx, focus) {
    const mf = this._mineFocusOf(ctx, focus);
    if (!mf.near) {
      // Alles freigeben — außer Sichtweite.
      if (this.activeMine.size > 0 || this.queueMine.length > 0) {
        for (const [idx, handle] of [...this.activeMine]) {
          this._releaseMine(idx, handle);
        }
        this.queueMine.length = 0;
        this.queueMineSet.clear();
        this.centerSegMine = null;
      }
      if (this.deadEndGroup) this.deadEndGroup.visible = false;
      if (this.junctionMarker) {
        // Weiche bleibt lesbar, sobald man in der Nähe der Haupt-Weiche ist.
        this.junctionMarker.visible = Math.abs(focus.s - JUNCTION_MAIN_S) < VIEW_RADIUS + 80;
      }
      return;
    }

    if (this.junctionMarker) this.junctionMarker.visible = true;
    if (this.deadEndGroup) {
      this.deadEndGroup.visible = Math.abs(mf.s - DEAD_END.s) < VIEW_RADIUS + 120
        || mf.s > MINE_TRACK_LENGTH - VIEW_RADIUS;
    }

    const seg = Math.floor(mf.s / SEG_LEN);
    if (seg !== this.centerSegMine) {
      this.centerSegMine = seg;
      this._syncDesiredMine(mf.s);
    }
    this._drainQueueMine(BUILDS_PER_FRAME);
  }

  /**
   * Ist der Spieler/Zug nah genug an der Nebenstrecke, und welche Mine-s?
   * Nutzt Weltposition → projectToTrack(mine) und Zug-Branch.
   */
  _mineFocusOf(ctx, focus) {
    _mineFocus.near = false;
    _mineFocus.s = 0;
    _mineFocus.x = focus.x;
    _mineFocus.y = focus.y;
    _mineFocus.z = focus.z;

    // Zug auf der Mine: Fenster um die Zugspitze (Spieler sitzt oft darauf).
    if (ctx.train?.branch === BRANCH_MINE && typeof ctx.train.headS === 'number') {
      _mineFocus.near = true;
      _mineFocus.s = ctx.train.headS;
      return _mineFocus;
    }

    // Nahe der Weiche auf der Hauptstrecke → Mine-Anfang sichtbar machen.
    const nearJunc = Math.abs(focus.s - JUNCTION_MAIN_S) < VIEW_RADIUS + 60;

    // Projektion auf Mine-Geometrie (Welt-XZ).
    const proj = projectToTrack(focus.x, focus.z, -1, BRANCH_MINE);
    const nearMineGeom = proj.distance < 90;

    if (nearJunc || nearMineGeom) {
      _mineFocus.near = true;
      // An der Weiche ohne Mine-Nähe: s≈0; sonst projizierte Bogenlänge.
      _mineFocus.s = nearMineGeom ? proj.s : 0;
      return _mineFocus;
    }

    return _mineFocus;
  }

  _syncDesiredMine(s) {
    const half = Math.ceil(VIEW_RADIUS / SEG_LEN);
    const center = Math.floor(s / SEG_LEN);
    const maxSeg = Math.floor((MINE_TRACK_LENGTH - 0.01) / SEG_LEN);
    const desired = new Set();

    for (let i = center - half; i <= center + half; i++) {
      if (i < 0 || i > maxSeg) continue;
      desired.add(i);
      if (!this.activeMine.has(i) && !this.queueMineSet.has(i)) {
        this.queueMine.push(i);
        this.queueMineSet.add(i);
      }
    }

    for (const [idx, handle] of this.activeMine) {
      if (!desired.has(idx)) this._releaseMine(idx, handle);
    }

    if (this.queueMine.length > 0) {
      const kept = [];
      for (const idx of this.queueMine) {
        if (desired.has(idx) && !this.activeMine.has(idx)) kept.push(idx);
        else this.queueMineSet.delete(idx);
      }
      this.queueMine = kept;
    }
  }

  _drainQueueMine(maxBuilds) {
    let built = 0;
    while (built < maxBuilds && this.queueMine.length > 0) {
      const idx = this.queueMine.shift();
      this.queueMineSet.delete(idx);
      if (this.activeMine.has(idx)) continue;
      if (this.centerSegMine != null) {
        const half = Math.ceil(VIEW_RADIUS / SEG_LEN);
        if (Math.abs(idx - this.centerSegMine) > half + 1) continue;
      }
      const handle = this._acquireMine();
      fillSegment(handle, idx, this._matsMine());
      this.mineGroup.add(handle.group);
      this.activeMine.set(idx, handle);
      built++;
    }
  }

  /**
   * Prellbock am Ende der Sackgasse — muss lesbar sein, sonst ist die Falle unfair
   * (Konzept §2.1: Isolation, hier ist Schluss).
   */
  _buildMineDeadEnd() {
    const s = MINE_TRACK_LENGTH - 2;
    trackFrame(s, _frame, BRANCH_MINE);
    const y = railTopY(s, 0, BRANCH_MINE);

    const g = new THREE.Group();
    g.name = 'dead-end-buffer';
    g.userData.midS = s;
    g.userData.branch = BRANCH_MINE;

    // Zwei Betonklötze + Querbalken — klassischer Prellbock, Silhouette ≥10 cm.
    const blockMat = mat('concreteDark');
    const steelMat = mat('railRust');
    for (const lat of [-HALF_GAUGE, HALF_GAUGE]) {
      const block = new THREE.Mesh(box(0.45, 0.85, 0.55), blockMat);
      block.position.set(
        _frame.pos.x + _frame.right.x * lat,
        y + 0.35,
        _frame.pos.z + _frame.right.z * lat
      );
      block.rotation.y = _frame.heading;
      block.castShadow = true;
      g.add(block);
    }
    // Querträger
    const beam = new THREE.Mesh(box(RAIL_GAUGE + 0.6, 0.22, 0.35), steelMat);
    beam.position.set(_frame.pos.x, y + 0.7, _frame.pos.z);
    beam.rotation.y = _frame.heading;
    beam.castShadow = true;
    g.add(beam);

    // Schotterwall dahinter — „hier endet die Welt".
    const wall = new THREE.Mesh(box(4.5, 1.4, 2.2), mat('ballastDark'));
    wall.position.set(
      _frame.pos.x - _frame.tangent.x * 2.5,
      y + 0.4,
      _frame.pos.z - _frame.tangent.z * 2.5
    );
    wall.rotation.y = _frame.heading;
    wall.receiveShadow = true;
    g.add(wall);

    // Optional: verfallener Verladepunkt (Trichter + Halde + Blechdach).
    this._buildAbandonedLoader(g, DEAD_END.s);

    g.visible = false;
    this.mineGroup.add(g);
    this.deadEndGroup = g;
  }

  /**
   * Statische Kollisionskörper für Prellbock, Verladung und toten Waggon.
   * Maße aus dem Bau — nicht aus dem Mesh messen.
   */
  _registerColliders(ctx) {
    const col = ctx.collision ?? ctx.systems?.collision;
    if (!col) return;

    // Prellbock am Mine-Ende
    const sEnd = MINE_TRACK_LENGTH - 2;
    trackFrame(sEnd, _frame, BRANCH_MINE);
    const yEnd = railTopY(sEnd, 0, BRANCH_MINE);
    // Querbalken + Klötze als eine niedrige Box
    col.addBox(
      _frame.pos.x, yEnd + 0.45, _frame.pos.z,
      (RAIL_GAUGE + 0.6) * 0.5, 0.45, 0.4,
      _frame.heading, COL_SOLID,
    );
    // Schotterwall dahinter
    const wx = _frame.pos.x - _frame.tangent.x * 2.5;
    const wz = _frame.pos.z - _frame.tangent.z * 2.5;
    col.addBox(wx, yEnd + 0.4, wz, 2.25, 0.7, 1.1, _frame.heading, COL_SOLID);

    // Verladepunkt DEAD_END
    const s = DEAD_END.s;
    trackFrame(s, _frame, BRANCH_MINE);
    const y = railTopY(s, 0, BRANCH_MINE);
    const lat = 7.5;
    const baseX = _frame.pos.x + _frame.right.x * lat;
    const baseZ = _frame.pos.z + _frame.right.z * lat;
    col.addBox(baseX, y + 2.4, baseZ, 2.75, 2.1, 3.5, _frame.heading, COL_SOLID);
    col.addBox(
      baseX + _frame.right.x * 4.5, y + 0.7, baseZ + _frame.right.z * 4.5,
      3.0, 0.9, 2.5, _frame.heading + 0.3, COL_SOLID | COL_STEP,
    );

    // Toter Waggon Nord
    const sd = STATIONS[1].sidings.find((x) => x.id === 'north-dead');
    if (sd) {
      const sc = (sd.s0 + sd.s1) * 0.5;
      const latC = sidingLateral(sc, sd);
      trackFrame(sc, _frame);
      const yc = railTopY(sc, latC);
      const cx = _frame.pos.x + _frame.right.x * latC;
      const cz = _frame.pos.z + _frame.right.z * latC;
      col.addBox(cx, yc + 2.0, cz, 1.5, 1.6, 6.0, _frame.heading, COL_SOLID);
    }
  }

  /** Aufgegebene Verladung an DEAD_END — tot, rostig, lesbar als „kein Ort mehr". */
  _buildAbandonedLoader(parent, s) {
    trackFrame(s, _frame, BRANCH_MINE);
    const y = railTopY(s, 0, BRANCH_MINE);
    const lat = 7.5;
    const baseX = _frame.pos.x + _frame.right.x * lat;
    const baseZ = _frame.pos.z + _frame.right.z * lat;

    // Trichter / Bunker-Silhouette
    const bunker = new THREE.Mesh(box(5.5, 4.2, 7.0), mat('corrugated'));
    bunker.position.set(baseX, y + 2.4, baseZ);
    bunker.rotation.y = _frame.heading;
    bunker.castShadow = true;
    parent.add(bunker);

    // Schiefes Blechdach
    const roof = new THREE.Mesh(box(6.5, 0.2, 8.0), mat('railRust'));
    roof.position.set(baseX, y + 4.7, baseZ);
    roof.rotation.y = _frame.heading;
    roof.rotation.z = 0.12;
    parent.add(roof);

    // Erz-/Abraumhalde
    const pile = new THREE.Mesh(box(6.0, 1.8, 5.0), mat('rockDark'));
    pile.position.set(
      baseX + _frame.right.x * 4.5,
      y + 0.7,
      baseZ + _frame.right.z * 4.5
    );
    pile.rotation.y = _frame.heading + 0.3;
    parent.add(pile);

    // Stützen des Trichters über dem Gleis (tot, nichts fällt mehr).
    const chute = new THREE.Mesh(box(2.0, 1.5, 2.8), mat('steelDark'));
    chute.position.set(
      _frame.pos.x + _frame.right.x * 3.2,
      y + 3.6,
      _frame.pos.z + _frame.right.z * 3.2
    );
    chute.rotation.y = _frame.heading;
    parent.add(chute);
  }

  /**
   * Weiche als erkennbbares Bauwerk (Konzept §12.1 industrielle Möblierung).
   * Der Spieler steht daneben und läuft darüber — Silhouette muss „Weiche"
   * lesen, nicht „zwei Wege kreuzen sich".
   *
   * Bauteile (Reihenfolge der Lesbarkeit):
   *  1. Zwei Zungen, die spitz am Backenschienenpaar anliegen und sich öffnen
   *  2. Herzstück mit Flügelschienen weiter hinten im Abzweig
   *  3. Langer Parallel-Bereich mit gemeinsamen, überlangen Schwellen
   *  4. Verrosteter Hand-Stellhebel am Rand (aufgegebene Strecke)
   *  5. Kleine Weichenlaterne (optional, billig)
   *
   * Ortsfest, ein Draw-Call-Bündel, Visibility wie Segment-Culling.
   */
  _buildJunctionMarker() {
    const g = new THREE.Group();
    g.name = 'junction:mine-switch';
    g.userData.midS = JUNCTION_MAIN_S;
    g.userData.branch = BRANCH_MAIN;

    // Weichenlänge entlang der Nebenstrecke: Zungen + Herz + Parallelzone.
    // Geometrie der Mine läuft hier schon realistisch flach ab (track.js).
    const SWITCH_LEN = 72;       // m Bogenlänge auf BRANCH_MINE
    const TONGUE_LEN = 12;       // Zungen spitz → geöffnet
    const FROG_S = 38;           // Herzstück-Mitte
    const LONG_SLEEPER_END = 58; // gemeinsame Schwellen bis hier

    // --- Abzweig-Schienen: Samples entlang der echten Mine-Geometrie ---------
    // (nicht lerp — die korrigierte Tangente ist der ganze Punkt des Fixes)
    const nSamp = 28;
    const samples = new Array(nSamp);
    for (let i = 0; i < nSamp; i++) {
      const t = i / (nSamp - 1);
      const sMine = t * SWITCH_LEN;
      trackFrame(sMine, _frame, BRANCH_MINE);
      samples[i] = {
        s: sMine,
        x: _frame.pos.x,
        y: _frame.pos.y,
        z: _frame.pos.z,
        tx: _frame.tangent.x,
        ty: _frame.tangent.y,
        tz: _frame.tangent.z,
        rx: _frame.right.x,
        rz: _frame.right.z,
        heading: _frame.heading,
        cant: 0,
        lateral: 0,
      };
    }

    // Abzweiggleis (beide Schienen) — rostig, aufgegebene Nebenbahn.
    const railGeo = buildDualRailGeometry(samples, { weathered: true, capacity: nSamp });
    const railMesh = new THREE.Mesh(railGeo, this.matRailRust);
    railMesh.receiveShadow = true;
    railMesh.name = 'switch-diverging-rails';
    g.add(railMesh);

    // --- Zungen: spitz am Stock, öffnen sich über TONGUE_LEN ------------------
    // Optisch das Erkennungsmerkmal. Zwei schmale Schienenköpfe, die vom
    // Innenraum der Spurweite an die Backenschienen wandern.
    // Ein InstancedMesh (1 Draw Call) statt 20 Einzelmeshes.
    const tongueMat = matShiny('railRust', { vertexColors: false });
    const tongueGeo = box(RAIL_W * 0.85, RAIL_H * 0.9, 1.0);
    const nTongue = 10;
    const tongueMesh = new THREE.InstancedMesh(tongueGeo, tongueMat, nTongue * 2);
    tongueMesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    tongueMesh.castShadow = true;
    tongueMesh.name = 'switch-tongues';
    let ti = 0;
    for (let side = -1; side <= 1; side += 2) {
      // side −1 = linke Zunge, +1 = rechte. Abzweig = +lateral.
      for (let i = 0; i < nTongue; i++) {
        const t = (i + 0.5) / nTongue;
        const sMine = t * TONGUE_LEN;
        trackFrame(sMine, _frame, BRANCH_MINE);
        // Start nahe Gleismitte, wandert zur Backenschiene (±HALF_GAUGE).
        const open = t * t * (3 - 2 * t);
        const lat = side * (0.18 + open * (HALF_GAUGE - 0.18));
        const y = _frame.pos.y + RAIL_TOP_OFFSET - RAIL_H * 0.45;
        _pos.set(
          _frame.pos.x + _frame.right.x * lat,
          y,
          _frame.pos.z + _frame.right.z * lat,
        );
        _euler.set(0, _frame.heading, 0);
        _quat.setFromEuler(_euler);
        _scale.set(1, 1, (TONGUE_LEN / nTongue) * 1.05);
        _mat4.compose(_pos, _quat, _scale);
        tongueMesh.setMatrixAt(ti++, _mat4);
      }
    }
    tongueMesh.instanceMatrix.needsUpdate = true;
    g.add(tongueMesh);

    // --- Herzstück + Flügelschienen ------------------------------------------
    // Kreuzung der Innenschienen: keilförmiger Block + zwei kurze Flügel.
    // Drei Teile in einer Gruppe → optisch ein Bauteil, 3 Draw Calls ok.
    trackFrame(FROG_S, _frame, BRANCH_MINE);
    const frogY = _frame.pos.y + RAIL_TOP_OFFSET - RAIL_H * 0.4;
    const frogMat = mat('railRust');
    const frogGroup = new THREE.Group();
    frogGroup.name = 'switch-frog';
    const frog = new THREE.Mesh(box(0.55, RAIL_H * 1.1, 2.4), frogMat);
    frog.position.set(_frame.pos.x, frogY, _frame.pos.z);
    frog.rotation.y = _frame.heading;
    frog.castShadow = true;
    frogGroup.add(frog);
    for (const lat of [-0.55, 0.55]) {
      const wing = new THREE.Mesh(box(RAIL_W * 0.9, RAIL_H, 1.8), frogMat);
      wing.position.set(
        _frame.pos.x + _frame.right.x * lat - _frame.tangent.x * 0.6,
        frogY,
        _frame.pos.z + _frame.right.z * lat - _frame.tangent.z * 0.6,
      );
      wing.rotation.y = _frame.heading + (lat > 0 ? 0.08 : -0.08);
      frogGroup.add(wing);
    }
    g.add(frogGroup);

    // --- Gemeinsame lange Schwellen (das Bild-Detail, das am meisten fehlte) -
    // Im Weichenbereich liegen beide Gleise dicht; Schwellen tragen beide und
    // sind deshalb länger als normal (3,6–4,8 m statt 2,5 m).
    const nLong = Math.floor(LONG_SLEEPER_END / SLEEPER_SPACING);
    const longSleeperGeo = box(1, SLEEPER_H, SLEEPER_W); // X wird pro Instanz skaliert
    const longSleepers = new THREE.InstancedMesh(longSleeperGeo, this.matSleeperWorn, nLong);
    longSleepers.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    longSleepers.name = 'switch-long-sleepers';
    let li = 0;
    for (let s = SLEEPER_SPACING * 0.5; s < LONG_SLEEPER_END && li < nLong; s += SLEEPER_SPACING) {
      trackFrame(s, _frame, BRANCH_MINE);
      // Abstand Mine↔Haupt wächst — Schwellenlänge folgt dem realen Versatz.
      // s auf der Haupt ist nur Näherung (gleiche Bogenlänge ab Weiche).
      const pMain = trackPoint(JUNCTION_MAIN_S + s * 0.92, _minePt, BRANCH_MAIN);
      const dx = _frame.pos.x - pMain.x;
      const dz = _frame.pos.z - pMain.z;
      const sep = Math.hypot(dx, dz);
      // Schwelle muss beide Gleismitten + Überstand decken.
      const len = Math.max(SLEEPER_LEN, Math.min(5.2, sep + RAIL_GAUGE + 0.8));
      const midX = (_frame.pos.x + pMain.x) * 0.5;
      const midZ = (_frame.pos.z + pMain.z) * 0.5;
      const midY = (_frame.pos.y + pMain.y) * 0.5
        + RAIL_TOP_OFFSET - RAIL_H - SLEEPER_H * 0.5;
      _pos.set(midX, midY, midZ);
      _euler.set(0, _frame.heading, 0);
      _quat.setFromEuler(_euler);
      _scale.set(len / 1.0, 1, 1); // geo-Breite 1 in X = Schwellenlänge
      _mat4.compose(_pos, _quat, _scale);
      longSleepers.setMatrixAt(li, _mat4);
      _color.setHex(varyColor('woodDark', (li + 3) * 0.11, 0.18));
      longSleepers.setColorAt(li, _color);
      li++;
    }
    longSleepers.count = li;
    longSleepers.instanceMatrix.needsUpdate = true;
    if (longSleepers.instanceColor) longSleepers.instanceColor.needsUpdate = true;
    g.add(longSleepers);

    // --- Weichenantrieb / Handhebel (industriell, verrostet) -----------------
    // Am Stockschienen-Rand, vor den Zungen — klassische Silhouette.
    trackFrame(JUNCTION_MAIN_S + 3, _frame, BRANCH_MAIN);
    const leverLat = -4.2; // links vom Hauptgleis, freier Stand
    const lx = _frame.pos.x + _frame.right.x * leverLat;
    const lz = _frame.pos.z + _frame.right.z * leverLat;
    const ly = railTopY(JUNCTION_MAIN_S + 3, 0, BRANCH_MAIN);
    const leverGroup = new THREE.Group();
    leverGroup.name = 'switch-lever';
    // Betonsockel
    const base = new THREE.Mesh(box(0.7, 0.35, 0.9), mat('concreteDark'));
    base.position.set(lx, ly - 0.05, lz);
    base.rotation.y = _frame.heading;
    base.castShadow = true;
    leverGroup.add(base);
    // Gehäuse / Antriebskasten
    const boxM = new THREE.Mesh(box(0.45, 0.55, 0.7), mat('railRust'));
    boxM.position.set(lx, ly + 0.4, lz);
    boxM.rotation.y = _frame.heading;
    boxM.castShadow = true;
    leverGroup.add(boxM);
    // Handhebel schräg nach oben (gestellt Richtung Abzweig — optisch „umgelegt")
    const handle = new THREE.Mesh(box(0.12, 1.35, 0.12), mat('steelDark'));
    handle.position.set(
      lx + _frame.right.x * 0.15 + _frame.tangent.x * 0.1,
      ly + 1.05,
      lz + _frame.right.z * 0.15 + _frame.tangent.z * 0.1,
    );
    handle.rotation.y = _frame.heading;
    handle.rotation.z = 0.55; // schräg
    handle.castShadow = true;
    leverGroup.add(handle);
    // Gegengewichtskugel am Hebelende
    const weight = new THREE.Mesh(box(0.28, 0.28, 0.28), mat('steelDark'));
    weight.position.set(
      lx + _frame.right.x * 0.55 + _frame.tangent.x * 0.15,
      ly + 1.55,
      lz + _frame.right.z * 0.55 + _frame.tangent.z * 0.15,
    );
    leverGroup.add(weight);
    g.add(leverGroup);

    // --- Weichenlaterne (billig, Silhouette) ---------------------------------
    trackFrame(JUNCTION_MAIN_S - 1.5, _frame, BRANCH_MAIN);
    const lampLat = -3.6;
    const lampX = _frame.pos.x + _frame.right.x * lampLat;
    const lampZ = _frame.pos.z + _frame.right.z * lampLat;
    const lampY = railTopY(JUNCTION_MAIN_S - 1.5, 0, BRANCH_MAIN);
    const mast = new THREE.Mesh(box(0.12, 2.4, 0.12), mat('steelDark'));
    mast.position.set(lampX, lampY + 1.1, lampZ);
    mast.castShadow = true;
    g.add(mast);
    const lamp = new THREE.Mesh(box(0.35, 0.4, 0.35), mat('railRust'));
    lamp.position.set(lampX, lampY + 2.35, lampZ);
    lamp.castShadow = true;
    g.add(lamp);

    g.visible = false;
    this.group.add(g);
    this.junctionMarker = g;
  }

  // --- Nebengleise ----------------------------------------------------------

  _buildSidings() {
    for (const st of STATIONS) {
      for (const sd of st.sidings) {
        this._buildSiding(sd);
      }
    }
    // Totes Nebengleis Nord: abgestellter Waggon (Konzept §7 — viel Zeit,
    // niemand sieht es). Eigene Malfläche, nicht Teil des fahrenden Zuges.
    this._buildDeadCar();
  }

  _buildDeadCar() {
    const sd = STATIONS[1].sidings.find((x) => x.id === 'north-dead');
    if (!sd) return;

    const s = (sd.s0 + sd.s1) * 0.5;
    const lat = sidingLateral(s, sd);
    trackFrame(s, _frame);
    const y = railTopY(s, lat);

    const car = new THREE.Group();
    car.name = 'dead-car';
    car.userData.midS = s;

    const bodyW = 3.0, bodyH = 3.2, bodyL = 12.0;
    const body = new THREE.Mesh(box(bodyW, bodyH, bodyL), mat('railRust'));
    body.position.y = bodyH * 0.5 + 0.4;
    car.add(body);

    const frame = new THREE.Mesh(box(bodyW * 0.9, 0.25, bodyL * 0.95), mat('steelDark'));
    frame.position.y = 0.45;
    car.add(frame);

    // Räder — nur Silhouette, ≥10 cm.
    const wheelGeo = new THREE.CylinderGeometry(0.42, 0.42, 0.18, 8);
    wheelGeo.rotateZ(Math.PI / 2);
    for (const [lx, lz] of [[-1.1, -4.2], [1.1, -4.2], [-1.1, 4.2], [1.1, 4.2]]) {
      const w = new THREE.Mesh(wheelGeo, mat('steelDark'));
      w.position.set(lx, 0.42, lz);
      car.add(w);
    }

    // Malfläche: „viel Zeit, niemand sieht es" — surfaceId laut Vertrag/Aufgabe.
    const surfW = 10.5, surfH = 2.4;
    const surf = new THREE.Mesh(box(0.12, surfH, surfW), mat('steel'));
    surf.position.set(bodyW * 0.5 + 0.06, 1.7, 0);
    surf.userData.surfaceId = 'siding:north-dead-car';
    surf.userData.paintSurface = {
      surfaceId: 'siding:north-dead-car',
      kind: 'car',
      widthM: surfW,
      heightM: surfH,
      centerY: 1.7,
      note: 'abgestellter Waggon, totes Nebengleis',
    };
    car.add(surf);

    car.position.set(
      _frame.pos.x + _frame.right.x * lat,
      y,
      _frame.pos.z + _frame.right.z * lat
    );
    car.rotation.y = _frame.heading;

    this.sidingGroup.add(car);
    this.deadCar = car;
  }

  _buildSiding(sd) {
    const g = new THREE.Group();
    g.name = `siding:${sd.id}`;
    g.userData.s0 = sd.s0;
    g.userData.s1 = sd.s1;
    g.userData.midS = (sd.s0 + sd.s1) * 0.5;

    const dead = sd.id.includes('dead');
    // Verwittert: mehr Rost, dunkleres Holz. Totes Gleis extra ungenutzt.
    const matRail = dead ? this.matRailRust : this.matRail;
    const matSleep = dead ? this.matSleeperWorn : this.matSleeper;
    const matBed = dead ? this.matBallastDark : this.matBallast;

    const step = 2.5;
    const nSamp = Math.ceil((sd.s1 - sd.s0) / step) + 1;
    const samples = new Array(nSamp);
    for (let i = 0; i < nSamp; i++) {
      const t = i / (nSamp - 1);
      const s = sd.s0 + t * (sd.s1 - sd.s0);
      const lat = sidingLateral(s, sd);
      trackFrame(s, _frame);
      samples[i] = {
        s,
        x: _frame.pos.x,
        y: _frame.pos.y,
        z: _frame.pos.z,
        tx: _frame.tangent.x,
        ty: _frame.tangent.y,
        tz: _frame.tangent.z,
        rx: _frame.right.x,
        rz: _frame.right.z,
        heading: _frame.heading,
        cant: 0, // Nebengleis flach — tot oder langsam, keine Überhöhung
        lateral: lat,
      };
    }

    const railGeo = buildDualRailGeometry(samples, {
      weathered: true,
      capacity: nSamp,
    });
    const railMesh = new THREE.Mesh(railGeo, matRail);
    railMesh.receiveShadow = true;
    g.add(railMesh);

    // Schwellen unregelmäßiger als auf der Hauptstrecke.
    const nSleep = Math.max(1, Math.floor((sd.s1 - sd.s0) / SLEEPER_SPACING));
    const sleepers = new THREE.InstancedMesh(this.sleeperGeo, matSleep, nSleep);
    sleepers.frustumCulled = true;
    sleepers.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    let si = 0;
    for (let s = sd.s0 + SLEEPER_SPACING * 0.5; s < sd.s1 && si < nSleep; s += SLEEPER_SPACING) {
      const lat = sidingLateral(s, sd);
      // Unregelmäßig: Lücken und Schräglage signalisieren „ungenutzt".
      const seed = hashSleeper(si + 9000, sd.id);
      if (dead && seed > 0.88) {
        // Fehlende Schwelle — das tote Gleis hat Lücken.
        continue;
      }
      placeSleeperMatrix(s, lat, _mat4, seed, dead ? 0.18 : 0.08);
      sleepers.setMatrixAt(si, _mat4);
      const col = varyColor(dead ? 'woodDark' : 'sleeper', seed, dead ? 0.22 : 0.14);
      _color.setHex(col);
      sleepers.setColorAt(si, _color);
      si++;
    }
    sleepers.count = si;
    sleepers.instanceMatrix.needsUpdate = true;
    if (sleepers.instanceColor) sleepers.instanceColor.needsUpdate = true;
    g.add(sleepers);

    const bedGeo = buildBallastGeometry(samples);
    g.add(new THREE.Mesh(bedGeo, matBed));

    // Bewuchs zwischen den Schwellen nur auf dem toten Nebengleis —
    // Konzept §7: sicher, viel Zeit, niemand sieht es. Muss ungenutzt wirken.
    if (dead) {
      g.add(buildSidingWeeds(sd, samples));
    }

    this.sidingGroup.add(g);
  }

  _updateSidingVisibility(s) {
    const RANGE = 480;
    for (const child of this.sidingGroup.children) {
      const mid = child.userData.midS ?? s;
      child.visible = Math.abs(s - mid) < RANGE;
    }
  }

  _focusOf(ctx) {
    if (ctx.playerPos) {
      _focus.x = ctx.playerPos.x;
      _focus.y = ctx.playerPos.y;
      _focus.z = ctx.playerPos.z;
      if (ctx.playerState?.trackS != null) _focus.s = ctx.playerState.trackS;
      else if (ctx.trackHint != null && ctx.trackHint >= 0) _focus.s = ctx.trackHint;
      else _focus.s = STATIONS[0].s;
      return _focus;
    }
    // Vor dem ersten Player-Update: Südbahnhof.
    _focus.x = _startPos.x;
    _focus.y = _startPos.y;
    _focus.z = _startPos.z;
    _focus.s = STATIONS[0].s;
    return _focus;
  }

  /** Aktive Segmente (für Tests / Debug). */
  getActiveSegments() {
    const out = [];
    for (const [idx, h] of this.active) out.push({ idx, s0: h.s0, s1: h.s1 });
    out.sort((a, b) => a.idx - b.idx);
    return out;
  }

  /** Erzwingt synchronen Bau um s (Tests). Alte Segmente außerhalb freigeben. */
  forceBuildAround(s, radius = VIEW_RADIUS) {
    this.centerSeg = Math.floor(s / SEG_LEN);
    const half = Math.ceil(radius / SEG_LEN);
    const maxSeg = Math.floor((TRACK_LENGTH - 0.01) / SEG_LEN);
    const desired = new Set();
    for (let i = this.centerSeg - half; i <= this.centerSeg + half; i++) {
      if (i < 0 || i > maxSeg) continue;
      desired.add(i);
      if (this.active.has(i)) continue;
      const handle = this._acquire();
      fillSegment(handle, i, this._matsMain());
      this.group.add(handle.group);
      this.active.set(i, handle);
    }
    for (const [idx, handle] of this.active) {
      if (!desired.has(idx)) this._release(idx, handle);
    }
  }

  /**
   * Schienenoberkante aus gebauter Geometrie (Tests).
   * Nimmt den Querschnitt mit minimalem |Δs| — nicht den räumlich nächsten
   * Vertex (der kann 2 m weiter auf der Steigung 30 mm höher liegen).
   */
  sampleBuiltRailTop(s) {
    const idx = Math.floor(s / SEG_LEN);
    const h = this.active.get(idx);
    if (!h?.railMesh || h.sampleCount < 2) return null;
    const pos = h.railMesh.geometry.attributes.position.array;
    // Sample-Index im Segment (gleichmäßig s0..s1).
    const t = (s - h.s0) / (h.s1 - h.s0 || 1);
    const si = Math.max(0, Math.min(h.sampleCount - 1, Math.round(t * (h.sampleCount - 1))));
    // 8 Vertices pro Sample (2 Schienen × 4), Oberkante = max Y.
    let bestY = -Infinity;
    const base = si * 8 * 3;
    for (let k = 0; k < 8; k++) {
      const y = pos[base + k * 3 + 1];
      if (y > bestY) bestY = y;
    }
    return bestY === -Infinity ? null : bestY;
  }

  /** Draw-Call-Schätzung (aktive Segmente × 3 + sichtbare Nebengleise + Mine). */
  estimateDrawCalls() {
    // Schienen (1) + Schwellen (1) + Bett (1) pro Segment.
    let n = this.active.size * 3;
    n += this.activeMine.size * 3;
    for (const c of this.sidingGroup.children) {
      if (c.visible) {
        // Sidings: rail + sleepers + bed (+ weeds auf dead).
        n += c.children.length;
      }
    }
    // Prellbock / Verladung / Weichenmarker
    if (this.deadEndGroup?.visible) n += this.deadEndGroup.children.length;
    if (this.junctionMarker?.visible) n += this.junctionMarker.children.length;
    return n;
  }

  /** Erzwingt Mine-Segmente um mineS (Tests). */
  forceBuildMineAround(mineS, radius = VIEW_RADIUS) {
    this.centerSegMine = Math.floor(mineS / SEG_LEN);
    const half = Math.ceil(radius / SEG_LEN);
    const maxSeg = Math.floor((MINE_TRACK_LENGTH - 0.01) / SEG_LEN);
    const desired = new Set();
    for (let i = this.centerSegMine - half; i <= this.centerSegMine + half; i++) {
      if (i < 0 || i > maxSeg) continue;
      desired.add(i);
      if (this.activeMine.has(i)) continue;
      const handle = this._acquireMine();
      fillSegment(handle, i, this._matsMine());
      this.mineGroup.add(handle.group);
      this.activeMine.set(i, handle);
    }
    for (const [idx, handle] of this.activeMine) {
      if (!desired.has(idx)) this._releaseMine(idx, handle);
    }
    if (this.deadEndGroup) this.deadEndGroup.visible = true;
    if (this.junctionMarker) this.junctionMarker.visible = true;
  }

  dispose() {
    for (const h of this.active.values()) disposeHandle(h);
    this.active.clear();
    for (const h of this.pool) disposeHandle(h);
    this.pool.length = 0;
    for (const h of this.activeMine.values()) disposeHandle(h);
    this.activeMine.clear();
    for (const h of this.poolMine) disposeHandle(h);
    this.poolMine.length = 0;
    this.sleeperGeo?.dispose?.();
    this.group?.parent?.remove(this.group);
  }
}

// --- Segment-Handle / Pool --------------------------------------------------

function createSegmentHandle(sleeperGeo) {
  const group = new THREE.Group();
  group.name = 'track-seg';

  // Voralloziierte Geometrien — beim Recycling nur Vertexdaten überschreiben.
  const railGeo = allocDualRailGeometry(MAX_SAMPLES);
  const railMesh = new THREE.Mesh(railGeo, []);
  railMesh.receiveShadow = true;
  group.add(railMesh);

  const sleepers = new THREE.InstancedMesh(sleeperGeo, null, MAX_SLEEPERS);
  sleepers.frustumCulled = true;
  sleepers.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  sleepers.castShadow = false;
  sleepers.receiveShadow = true;
  sleepers.count = 0;
  group.add(sleepers);

  const bedGeo = allocBallastGeometry(MAX_SAMPLES);
  const ballast = new THREE.Mesh(bedGeo, null);
  ballast.receiveShadow = true;
  group.add(ballast);

  return {
    group,
    idx: -1,
    s0: 0,
    s1: 0,
    sampleCount: 0,
    railMesh,
    sleepers,
    ballast,
    railGeo,
    bedGeo,
  };
}

function disposeHandle(handle) {
  handle.railGeo?.dispose?.();
  handle.bedGeo?.dispose?.();
  handle.sleepers?.dispose?.();
}

// --- Segment füllen ---------------------------------------------------------

/**
 * Schreibt Schienen, Schwellen und Bett für Segment-Index idx in den Handle.
 * Exportiert für check-world.js — rein mathematisch, braucht kein WebGL.
 */
export function fillSegment(handle, idx, mats) {
  const branch = mats.branch ?? BRANCH_MAIN;
  const trackLen = branch === BRANCH_MINE ? MINE_TRACK_LENGTH : TRACK_LENGTH;
  const s0 = idx * SEG_LEN;
  const s1 = Math.min(trackLen, s0 + SEG_LEN);
  handle.idx = idx;
  handle.s0 = s0;
  handle.s1 = s1;
  handle.branch = branch;
  handle.group.visible = true;
  handle.group.userData.s0 = s0;
  handle.group.userData.s1 = s1;
  handle.group.userData.branch = branch;

  const n = sampleBranchInto(_samples, s0, s1, SAMPLE_STEP, branch);
  handle.sampleCount = n;
  const samples = _samples;

  // Schienen (beide in einer Geo, Vertexfarben für Kopf/Flanke).
  writeDualRailGeometry(handle.railGeo, samples, n, { weathered: !!mats.weathered });
  handle.railMesh.material = mats.matRail;
  handle.railMesh.geometry = handle.railGeo;

  // Schwellen: erste bei ceil(s0/sp)*sp, Ende exklusiv s1 — Naht ohne Doppelung.
  // Mine: mehr Lücken und Jitter → verwahrlost lesbar.
  const weathered = !!mats.weathered || branch === BRANCH_MINE;
  const sp = SLEEPER_SPACING;
  const first = Math.ceil(s0 / sp - 1e-9) * sp;
  const tag = branch === BRANCH_MINE ? 'mine' : 'main';
  let si = 0;
  for (let s = first; s < s1 - 1e-6 && si < MAX_SLEEPERS; s += sp) {
    const seed = hashSleeper(Math.round(s / sp), tag);
    // Fehlende Schwellen auf der Mine (~12 %) — aufgegebenes Gleis.
    if (weathered && branch === BRANCH_MINE && seed > 0.88) continue;
    placeSleeperMatrix(s, 0, _mat4, seed, weathered ? 0.14 : 0.04, branch);
    handle.sleepers.setMatrixAt(si, _mat4);
    _color.setHex(varyColor(
      branch === BRANCH_MINE ? 'woodDark' : 'sleeper',
      seed,
      weathered ? 0.2 : 0.12,
    ));
    handle.sleepers.setColorAt(si, _color);
    si++;
  }
  handle.sleepers.count = si;
  handle.sleepers.material = mats.matSleeper;
  handle.sleepers.instanceMatrix.needsUpdate = true;
  if (handle.sleepers.instanceColor) handle.sleepers.instanceColor.needsUpdate = true;

  writeBallastGeometry(handle.bedGeo, samples, n);
  handle.ballast.material = mats.matBallast;
  handle.ballast.geometry = handle.bedGeo;

  return handle;
}

// --- Sampling ---------------------------------------------------------------

/**
 * Schreibt Stützpunkte einer Strecke in voralloziertes Array.
 * @returns {number} Anzahl Samples
 */
export function sampleBranchInto(out, s0, s1, step, branch = BRANCH_MAIN) {
  const n = Math.max(1, Math.ceil((s1 - s0) / step)) + 1;
  for (let i = 0; i < n; i++) {
    const s = i === n - 1 ? s1 : s0 + (i / (n - 1)) * (s1 - s0);
    trackFrame(s, _frame, branch);
    const sm = out[i];
    sm.s = s;
    sm.x = _frame.pos.x;
    sm.y = _frame.pos.y;
    sm.z = _frame.pos.z;
    sm.tx = _frame.tangent.x;
    sm.ty = _frame.tangent.y;
    sm.tz = _frame.tangent.z;
    sm.rx = _frame.right.x;
    sm.rz = _frame.right.z;
    sm.heading = _frame.heading;
    // Mine: keine Überhöhung — langsam, verwahrlost, flach verlegt.
    sm.cant = branch === BRANCH_MINE ? 0 : trackCant(s, branch);
    sm.lateral = 0;
  }
  return n;
}

/** Abwärtskompatibel: Hauptstrecke. */
export function sampleMainlineInto(out, s0, s1, step) {
  return sampleBranchInto(out, s0, s1, step, BRANCH_MAIN);
}

/** Komfort-Wrapper der eine neue Array-Liste liefert (Tests). */
export function sampleMainline(s0, s1, step = SAMPLE_STEP) {
  const n = Math.max(1, Math.ceil((s1 - s0) / step)) + 1;
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = {
      s: 0, x: 0, y: 0, z: 0, tx: 0, ty: 0, tz: 0,
      rx: 0, rz: 0, heading: 0, cant: 0, lateral: 0,
    };
  }
  sampleMainlineInto(out, s0, s1, step);
  return out;
}

/**
 * Lateral des Nebengleises mit sanftem Anlauf an den Enden.
 * Cosinus-Ease (statt smoothstep): startet mit Steigung 0 (tangential am
 * Hauptgleis), öffnet mit annähernd konstantem Abzweigwinkel ~7–9°, endet
 * flach parallel. Kurze Gleise begrenzen den Taper auf 35 % der Länge.
 */
export function sidingLateral(s, sd) {
  const full = sd.lateral;
  const span = sd.s1 - sd.s0;
  // Kurzes totes Gleis: Taper darf nicht beide Enden auffressen.
  const taper = Math.min(SIDING_TAPER, Math.max(24, span * 0.35));
  const d0 = s - sd.s0;
  const d1 = sd.s1 - s;
  let u = 1;
  if (d0 < taper) u = Math.min(u, cosineEase01(d0 / taper));
  if (d1 < taper) u = Math.min(u, cosineEase01(d1 / taper));
  return full * u;
}

/** 0→1 mit Ableitung 0 an den Enden (C¹), flacher als smoothstep in der Mitte. */
function cosineEase01(t) {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return 0.5 - 0.5 * Math.cos(Math.PI * x);
}

// --- Schienen-Geometrie -----------------------------------------------------

/**
 * Beide Schienen in einer BufferGeometry.
 * Groups: 0 = Flanken (Rost), 1 = Kopf (blank/glänzend).
 * 4 Vertices pro Querschnitt × 2 Schienen pro Sample.
 */
function allocDualRailGeometry(maxSamples) {
  const vertsPerSample = 8; // 4 + 4
  const maxV = maxSamples * vertsPerSample;
  // Pro Segment zwischen Samples: 2 Schienen × 4 Seiten × 2 Tris = 16 Tris,
  // aber top-faces separat: sides 2 rails × 3 faces × 2 = 12, tops 2 × 2 = 4.
  const maxIdx = (maxSamples - 1) * 16 * 3;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(maxV * 3), 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(maxV * 3), 3));
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(maxV * 3), 3));
  geo.setIndex(new THREE.BufferAttribute(new Uint32Array(maxIdx), 1));
  geo.userData.maxSamples = maxSamples;
  geo.userData.maxV = maxV;
  return geo;
}

function buildDualRailGeometry(samples, opts = {}) {
  const n = samples.length;
  const geo = allocDualRailGeometry(opts.capacity ?? n);
  writeDualRailGeometry(geo, samples, n, opts);
  return geo;
}

/**
 * Schreibt duale Schienenkurve. Exportiert für Tests (Naht, Spurweite, NaN).
 */
export function writeDualRailGeometry(geo, samples, n, opts = {}) {
  const weathered = !!opts.weathered;
  const positions = geo.attributes.position.array;
  const normals = geo.attributes.normal.array;
  const colors = geo.attributes.color.array;
  const index = geo.index.array;

  const hw = RAIL_W * 0.5;
  const hh = RAIL_H * 0.5;
  // Querschnitt: 0 BL, 1 BR, 2 TR, 3 TL (Blick in Fahrtrichtung).
  const local = [
    [-hw, -hh],
    [+hw, -hh],
    [+hw, +hh],
    [-hw, +hh],
  ];

  // Farben: Kopf blanker (heller), Flanken rostig. Bei verwittert alles rostiger.
  const topR = weathered ? 0.55 : 0.85;
  const topG = weathered ? 0.42 : 0.80;
  const topB = weathered ? 0.35 : 0.72;
  const sideR = weathered ? 0.42 : 0.48;
  const sideG = weathered ? 0.28 : 0.32;
  const sideB = weathered ? 0.20 : 0.24;

  for (let i = 0; i < n; i++) {
    const sm = samples[i];
    const baseLat = sm.lateral ?? 0;
    const cant = sm.cant;

    // Gekippte up/right-Achsen (Überhöhung).
    let ux = sm.rx * cant;
    let uy = 1;
    let uz = sm.rz * cant;
    let ulen = Math.hypot(ux, uy, uz) || 1;
    ux /= ulen; uy /= ulen; uz /= ulen;

    let rpx = sm.rx, rpy = 0, rpz = sm.rz;
    const dot = rpx * ux + rpy * uy + rpz * uz;
    rpx -= ux * dot; rpy -= uy * dot; rpz -= uz * dot;
    const rlen = Math.hypot(rpx, rpy, rpz) || 1;
    rpx /= rlen; rpy /= rlen; rpz /= rlen;

    for (let rail = 0; rail < 2; rail++) {
      const lat = baseLat + (rail === 0 ? -HALF_GAUGE : HALF_GAUGE);
      const topY = sm.y + RAIL_TOP_OFFSET + lat * cant;
      const cy = topY - hh;
      const cx = sm.x + sm.rx * lat;
      const cz = sm.z + sm.rz * lat;
      // Vertex-Basisindex: sample i, rail 0|1 → i*8 + rail*4
      const base = (i * 8 + rail * 4) * 3;

      for (let k = 0; k < 4; k++) {
        const lx = local[k][0];
        const ly = local[k][1];
        const vi = base + k * 3;
        positions[vi] = cx + rpx * lx + ux * ly;
        positions[vi + 1] = cy + rpy * lx + uy * ly;
        positions[vi + 2] = cz + rpz * lx + uz * ly;

        // Normale: oben → up, seitlich → ±right, unten → -up.
        let nx, ny, nz;
        if (k >= 2) {
          // Oberkante
          nx = ux; ny = uy; nz = uz;
          colors[vi] = topR; colors[vi + 1] = topG; colors[vi + 2] = topB;
        } else {
          nx = -ux; ny = -uy; nz = -uz;
          colors[vi] = sideR; colors[vi + 1] = sideG; colors[vi + 2] = sideB;
        }
        // Seiten-Vertices: k=0,1 sind unten; für Seitenflächen nutzen wir
        // die laterale Komponente in den Indices — Vertexnormale hier grob.
        if (k === 0 || k === 1) {
          // leicht nach außen kippen
          const sign = k === 0 ? -1 : 1;
          nx = rpx * sign * 0.7 - ux * 0.3;
          ny = rpy * sign * 0.7 - uy * 0.3;
          nz = rpz * sign * 0.7 - uz * 0.3;
        } else {
          const sign = k === 3 ? -1 : 1;
          // Top-Ecken: überwiegend up, etwas lateral
          nx = ux + rpx * sign * 0.15;
          ny = uy + rpy * sign * 0.15;
          nz = uz + rpz * sign * 0.15;
          colors[vi] = topR; colors[vi + 1] = topG; colors[vi + 2] = topB;
        }
        const nlen = Math.hypot(nx, ny, nz) || 1;
        normals[vi] = nx / nlen;
        normals[vi + 1] = ny / nlen;
        normals[vi + 2] = nz / nlen;
      }
    }
  }

  // Alle 4 Seiten des Querschnitts (unten, rechts, oben, links).
  let ii = 0;
  const edges = [[0, 1], [1, 2], [2, 3], [3, 0]];

  function emitQuad(i, rail, a, b) {
    const a0 = i * 8 + rail * 4 + a;
    const b0 = i * 8 + rail * 4 + b;
    const a1 = (i + 1) * 8 + rail * 4 + a;
    const b1 = (i + 1) * 8 + rail * 4 + b;
    index[ii++] = a0; index[ii++] = b0; index[ii++] = a1;
    index[ii++] = b0; index[ii++] = b1; index[ii++] = a1;
  }

  for (let i = 0; i < n - 1; i++) {
    for (let rail = 0; rail < 2; rail++) {
      for (const [a, b] of edges) emitQuad(i, rail, a, b);
    }
  }

  for (let k = ii; k < index.length; k++) index[k] = 0;

  geo.attributes.position.needsUpdate = true;
  geo.attributes.normal.needsUpdate = true;
  geo.attributes.color.needsUpdate = true;
  geo.index.needsUpdate = true;
  geo.setDrawRange(0, ii);
  geo.clearGroups();
  geo.addGroup(0, ii, 0);

  geo.computeBoundingSphere();
  return geo;
}

// --- Schotterbett (Trapezprofil) --------------------------------------------

function allocBallastGeometry(maxSamples) {
  // 4 Vertices pro Querschnitt (außen-unten L/R, oben L/R).
  const maxV = maxSamples * 4;
  const maxIdx = (maxSamples - 1) * 8 * 3; // 4 Seiten × 2 Tris, grob
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(maxV * 3), 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(maxV * 3), 3));
  geo.setIndex(new THREE.BufferAttribute(new Uint32Array(maxIdx), 1));
  return geo;
}

function buildBallastGeometry(samples) {
  const geo = allocBallastGeometry(samples.length);
  writeBallastGeometry(geo, samples, samples.length);
  return geo;
}

export function writeBallastGeometry(geo, samples, n) {
  const positions = geo.attributes.position.array;
  const normals = geo.attributes.normal.array;
  const index = geo.index.array;

  const topH = BALLAST_H;
  const botYOff = -0.06;

  for (let i = 0; i < n; i++) {
    const sm = samples[i];
    const lat0 = sm.lateral ?? 0;
    const cant = sm.cant ?? 0;
    // Bett folgt grob der Überhöhung, bleibt aber unter den Schwellen.
    const yTop = sm.y + botYOff + RAIL_TOP_OFFSET - RAIL_H - SLEEPER_H - 0.02;
    const yBot = yTop - topH * 0.55;

    // 0 = botL, 1 = botR, 2 = topR, 3 = topL
    const verts = [
      [lat0 - BALLAST_BOT_HALF, yBot],
      [lat0 + BALLAST_BOT_HALF, yBot],
      [lat0 + BALLAST_TOP_HALF, yTop],
      [lat0 - BALLAST_TOP_HALF, yTop],
    ];
    const base = i * 4 * 3;
    for (let k = 0; k < 4; k++) {
      const lat = verts[k][0];
      const y = verts[k][1] + lat * cant * 0.5;
      positions[base + k * 3] = sm.x + sm.rx * lat;
      positions[base + k * 3 + 1] = y;
      positions[base + k * 3 + 2] = sm.z + sm.rz * lat;
    }
    // Normalen: oben up, Seiten grob nach außen.
    for (let k = 0; k < 4; k++) {
      const vi = base + k * 3;
      if (k >= 2) {
        normals[vi] = 0; normals[vi + 1] = 1; normals[vi + 2] = 0;
      } else {
        const sign = k === 0 ? -1 : 1;
        normals[vi] = sm.rx * sign;
        normals[vi + 1] = 0.4;
        normals[vi + 2] = sm.rz * sign;
        const nl = Math.hypot(normals[vi], normals[vi + 1], normals[vi + 2]) || 1;
        normals[vi] /= nl; normals[vi + 1] /= nl; normals[vi + 2] /= nl;
      }
    }
  }

  let ii = 0;
  const faces = [[0, 1], [1, 2], [2, 3], [3, 0]];
  for (let i = 0; i < n - 1; i++) {
    for (const [a, b] of faces) {
      const a0 = i * 4 + a;
      const b0 = i * 4 + b;
      const a1 = (i + 1) * 4 + a;
      const b1 = (i + 1) * 4 + b;
      index[ii++] = a0; index[ii++] = b0; index[ii++] = a1;
      index[ii++] = b0; index[ii++] = b1; index[ii++] = a1;
    }
  }
  for (let k = ii; k < index.length; k++) index[k] = 0;

  geo.attributes.position.needsUpdate = true;
  geo.attributes.normal.needsUpdate = true;
  geo.index.needsUpdate = true;
  geo.setDrawRange(0, ii);
  geo.computeBoundingSphere();
  return geo;
}

// --- Schwellen --------------------------------------------------------------

/**
 * Deterministischer Seed aus Schwellennummer — gleiche Welt für alle Spieler.
 */
function hashSleeper(n, tag) {
  // Einfacher 32-bit Mix, ohne shared/rng importieren zu müssen (ohnehin ok).
  let h = (n | 0) * 747796405 + (tag === 'main' ? 0x9e3779b9 : 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function placeSleeperMatrix(s, lateral, outMat, seed = 0.5, jitter = 0.04, branch = BRANCH_MAIN) {
  trackFrame(s, _frame, branch);
  const cant = branch === BRANCH_MINE ? 0 : trackCant(s, branch);
  // Schwellenoberkante knapp unter Schienenfuß.
  const y = _frame.pos.y + RAIL_TOP_OFFSET - RAIL_H - SLEEPER_H * 0.5
    + lateral * cant;
  _pos.set(
    _frame.pos.x + _frame.right.x * lateral,
    y,
    _frame.pos.z + _frame.right.z * lateral
  );
  // Leichte Gier-Unregelmäßigkeit — handverlegt, nicht CAD.
  const yawJitter = (seed - 0.5) * jitter * 2;
  // Cant: das ganze Gleis kippt in der Kurve (gebaute vs. gezeichnete Strecke).
  _euler.set(0, _frame.heading + yawJitter, -cant);
  _quat.setFromEuler(_euler);
  _scale.set(1, 1, 1);
  outMat.compose(_pos, _quat, _scale);
  return outMat;
}

// --- Bewuchs auf totem Nebengleis -------------------------------------------

function buildSidingWeeds(sd, samples) {
  const group = new THREE.Group();
  group.name = 'siding-weeds';
  // Einfache low-poly Büsche als einzelne Meshes — nur ein totes Gleis, ~20 Stück.
  const bushGeo = new THREE.ConeGeometry(0.35, 0.55, 5);
  const bushMat = mat('scrub');
  const n = 18;
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    const s = sd.s0 + t * (sd.s1 - sd.s0);
    const lat = sidingLateral(s, sd);
    // Zwischen den Schienen und am Rand.
    const side = (i % 3) - 1; // -1, 0, 1
    const off = side * 0.55 + (hashSleeper(i, 'weed') - 0.5) * 0.3;
    trackFrame(s, _frame);
    const m = new THREE.Mesh(bushGeo, bushMat);
    m.position.set(
      _frame.pos.x + _frame.right.x * (lat + off),
      _frame.pos.y + 0.15,
      _frame.pos.z + _frame.right.z * (lat + off)
    );
    m.scale.setScalar(0.6 + hashSleeper(i + 3, 'weed') * 0.7);
    m.rotation.y = hashSleeper(i + 7, 'weed') * Math.PI * 2;
    group.add(m);
  }
  void samples;
  return group;
}

// --- Exports für Tests / andere Systeme -------------------------------------

export const TRACK_SEG_LEN = SEG_LEN;
export const TRACK_VIEW_RADIUS = VIEW_RADIUS;
export const TRACK_RAIL_H = RAIL_H;
export const TRACK_RAIL_TOP_OFFSET = RAIL_TOP_OFFSET;
export const TRACK_HALF_GAUGE = HALF_GAUGE;
export const TRACK_RAIL_W = RAIL_W;

/**
 * Spurweiten-Messung an Sample-Punkten (Tests).
 * Liefert Abstände zwischen linker und rechter Schienenmitte.
 */
export function measureGaugeAtSamples(samples) {
  const out = [];
  for (let i = 0; i < samples.length; i++) {
    const sm = samples[i];
    const baseLat = sm.lateral ?? 0;
    const lx = sm.x + sm.rx * (baseLat - HALF_GAUGE);
    const lz = sm.z + sm.rz * (baseLat - HALF_GAUGE);
    const rx = sm.x + sm.rx * (baseLat + HALF_GAUGE);
    const rz = sm.z + sm.rz * (baseLat + HALF_GAUGE);
    out.push(Math.hypot(rx - lx, rz - lz));
  }
  return out;
}

/**
 * Extrahiert die Weltposition der Schienenoberkante-Mitte (Tests).
 * rail: -1 links, +1 rechts.
 */
export function railCenterTop(sm, railSign) {
  const baseLat = sm.lateral ?? 0;
  const lat = baseLat + railSign * HALF_GAUGE;
  const cant = sm.cant ?? 0;
  return {
    x: sm.x + sm.rx * lat,
    y: sm.y + RAIL_TOP_OFFSET + lat * cant,
    z: sm.z + sm.rz * lat,
  };
}
